/**
 * Tipos da Matriz de Transição entre Playbooks (Brief 4 · Gestor · ADR-031 Eixo O).
 *
 * Config DECLARATIVA do Gestor: qual policy rege a troca de um playbook de origem
 * (`from`) para um de destino (`to`). NÃO confundir com `ai-transition` (runtime do
 * transition-agent) nem com o histórico `PlaybookTransitionProposal`.
 *
 * Não há recurso/model próprio de matriz: a policy vive INLINE em cada playbook
 * (`transitionPolicy.pairs[]`). Por isso esta feature reusa o wire de Playbooks —
 * os tipos abaixo derivam de `PlaybookDto['transitionPolicy']`, sem duplicar enum.
 *
 * R15 ao vivo (GET/PATCH /v1/playbooks): o enum de `policy` é snake_case
 * (`automatic_silent` / `requires_confirmation` / `blocked`) — o gabarito de design
 * usa hífen; a fonte é o wire. O PATCH SUBSTITUI o objeto `transitionPolicy` inteiro
 * (espelha o buildPatch do editor de Personas) com If-Match na `version` do `from`.
 */

import type { PlaybookDto } from '../playbooks/types';

/** Forma exata do par no wire (deriva do DTO de Playbook — fonte única). */
export type TransitionPair = PlaybookDto['transitionPolicy']['pairs'][number];

/**
 * As 3 policies canônicas (ADR-031). Derivadas como `string` no wire (o backend
 * declara `policy: string`), mas estreitadas aqui para o editor de 3 estados.
 * `automatic_silent`: transition-agent decide e executa (audit estruturado).
 * `requires_confirmation`: humano confirma a transição.
 * `blocked`: troca sempre manual; transition-agent não pode trocar sozinho.
 */
export type TransitionPolicy = 'automatic_silent' | 'requires_confirmation' | 'blocked';

export const TRANSITION_POLICIES: TransitionPolicy[] = [
  'automatic_silent',
  'requires_confirmation',
  'blocked',
];

export interface PolicyMeta {
  policy: TransitionPolicy;
  label: string;
  short: string;
  icon: string;
  desc: string;
  /** Classe CSS do swatch/célula (escopada sob .transition-matrix-screen). */
  cls: string;
}

export const POLICY_META: Record<TransitionPolicy, PolicyMeta> = {
  automatic_silent: {
    policy: 'automatic_silent',
    label: 'Automática silenciosa',
    short: 'auto',
    icon: '✓',
    desc: 'transition-agent decide e executa, com audit estruturado.',
    cls: 'tm-cell--auto',
  },
  requires_confirmation: {
    policy: 'requires_confirmation',
    label: 'Confirmação humana',
    short: 'confirma',
    icon: '⚠',
    desc: 'A IA propõe a troca; um humano confirma.',
    cls: 'tm-cell--confirm',
  },
  blocked: {
    policy: 'blocked',
    label: 'Bloqueada',
    short: 'bloqueia',
    icon: '🚫',
    desc: 'Troca sempre manual; a IA não pode mudar de playbook sozinha.',
    cls: 'tm-cell--blocked',
  },
};

export function isTransitionPolicy(value: string): value is TransitionPolicy {
  return value === 'automatic_silent' || value === 'requires_confirmation' || value === 'blocked';
}
